import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircleIcon,
  CheckCircle2Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
} from 'lucide-react';
import type {
  TimeChimpBeheerStatus,
  TimeChimpKoppelStatus,
  TimeChimpMedewerker,
  TimeChimpRapportageRegel,
} from '@kennisbank/shared';
import { Layout } from '../components/Layout.js';
import { apiGet, apiPost, apiPut, ApiError } from '../api/client.js';
import { suggesties } from '../lib/naam-match.js';

/**
 * Beheer: TimeChimp-koppeling: connectiestatus, de koppeling per medewerker en
 * het weekoverzicht "wie heeft geschreven/aangeboden" naast de contracturen.
 * Leeft live op de TimeChimp API; dit is bewust geen kopie van de
 * overzichtenmodule in TimeChimp zelf.
 */

function toYmd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function mondayOf(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(12, 0, 0, 0);
  copy.setDate(copy.getDate() - ((copy.getDay() + 6) % 7));
  return copy;
}

function addDays(d: Date, n: number): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + n);
  return copy;
}

const STATUS_LABEL: Record<TimeChimpKoppelStatus, string> = {
  automatisch: 'Automatisch',
  handmatig: 'Handmatig',
  verlopen: 'Verlopen',
  geen: 'Geen koppeling',
  ongekoppeld: 'Nog niet gekoppeld',
};

const STATUS_STIJL: Record<TimeChimpKoppelStatus, string> = {
  automatisch: 'bg-brand-50 text-brand-900',
  handmatig: 'bg-brand-50 text-brand-900',
  verlopen: 'bg-red-50 text-red-700',
  geen: 'bg-ink-900/5 text-ink-900/65',
  ongekoppeld: 'bg-amber-50 text-amber-800',
};

interface BeheerProject {
  id: number;
  name: string;
  customerName: string | null;
}

export function BeheerTijdregistratiePage() {
  const [status, setStatus] = useState<TimeChimpBeheerStatus | null>(null);
  const [rows, setRows] = useState<TimeChimpRapportageRegel[]>([]);
  const [monday, setMonday] = useState(() => mondayOf(new Date()));
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [notAvailable, setNotAvailable] = useState(false);
  const [projecten, setProjecten] = useState<BeheerProject[]>([]);
  const [refreshProjectId, setRefreshProjectId] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null);
  const [koppelMsg, setKoppelMsg] = useState<string | null>(null);
  const [bezig, setBezig] = useState<string | null>(null);

  const range = useMemo(() => ({ from: toYmd(monday), to: toYmd(addDays(monday, 6)) }), [monday]);

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await apiGet<TimeChimpBeheerStatus>('/api/v1/beheer/timechimp/status'));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setNotAvailable(true);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiGet<{ projects: BeheerProject[] }>(
          '/api/v1/beheer/timechimp/toewijzingen/projecten',
        );
        setProjecten(resp.projects);
      } catch {
        // Zonder projectenlijst vervalt alleen de verversknop; de rest van de
        // pagina blijft bruikbaar.
      }
    })();
  }, []);

  async function ververs() {
    const projectId = Number(refreshProjectId);
    if (!projectId) return;
    setRefreshing(true);
    setRefreshMsg(null);
    try {
      const resp = await apiPost<{ userCount: number }>(
        '/api/v1/beheer/timechimp/toewijzingen/project',
        { projectId },
      );
      setRefreshMsg(
        `Bijgewerkt: ${resp.userCount} ${resp.userCount === 1 ? 'medewerker ziet' : 'medewerkers zien'} dit project nu in de keuzelijst.`,
      );
      await loadStatus();
    } catch (err) {
      setRefreshMsg(
        err instanceof ApiError
          ? `Verversen mislukt (${err.status}): ${err.message}`
          : 'Verversen mislukt.',
      );
    } finally {
      setRefreshing(false);
    }
  }

  // Wat aandacht vraagt bovenaan: verlopen en ongekoppeld eerst, daarna op naam.
  const gesorteerd = useMemo(() => {
    const volgorde: Record<TimeChimpKoppelStatus, number> = {
      verlopen: 0,
      ongekoppeld: 1,
      handmatig: 2,
      automatisch: 3,
      geen: 4,
    };
    return [...(status?.medewerkers ?? [])].sort(
      (a, b) =>
        volgorde[a.status] - volgorde[b.status] || a.displayName.localeCompare(b.displayName, 'nl'),
    );
  }, [status]);

  const telling = useMemo(() => {
    const n = (s: TimeChimpKoppelStatus) => gesorteerd.filter((m) => m.status === s).length;
    const delen = [
      n('verlopen') > 0 ? `${n('verlopen')} verlopen` : null,
      n('ongekoppeld') > 0 ? `${n('ongekoppeld')} nog niet gekoppeld` : null,
      n('geen') > 0 ? `${n('geen')} zonder koppeling` : null,
    ].filter(Boolean);
    return delen.length > 0 ? `Nu: ${delen.join(', ')}.` : 'Alle medewerkers zijn gekoppeld.';
  }, [gesorteerd]);

  async function koppel(m: TimeChimpMedewerker, waarde: string) {
    setBezig(m.userId);
    setKoppelMsg(null);
    try {
      const body =
        waarde === 'geen'
          ? { state: 'geen' as const }
          : waarde === ''
            ? { state: 'onbepaald' as const }
            : { state: 'gekoppeld' as const, timechimpUserId: Number(waarde) };
      await apiPut('/api/v1/beheer/timechimp/koppeling/' + m.userId, body);
      setKoppelMsg(`Koppeling van ${m.displayName} bijgewerkt.`);
      await loadStatus();
    } catch (err) {
      setKoppelMsg(
        err instanceof ApiError ? `Koppelen mislukt: ${err.message}` : 'Koppelen mislukt.',
      );
    } finally {
      setBezig(null);
    }
  }

  const loadRapportage = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const resp = await apiGet<{ rows: TimeChimpRapportageRegel[] }>(
        '/api/v1/beheer/timechimp/rapportage',
        range,
      );
      setRows(resp.rows);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setNotAvailable(true);
      } else {
        setErrorMsg(
          err instanceof ApiError
            ? `Laden mislukt (${err.status}): ${err.message}`
            : 'Rapportage kon niet geladen worden.',
        );
      }
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    void loadRapportage();
  }, [loadRapportage]);

  const weekTitle = `${monday.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' })} t/m ${addDays(monday, 6).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}`;

  if (notAvailable) {
    return (
      <Layout>
        <div className="mx-auto w-full max-w-3xl px-4 py-6">
          <div className="rounded-2xl border border-ring2 bg-white px-6 py-10 text-center text-sm text-ink-900/65">
            De TimeChimp-koppeling staat nog uit (TIMECHIMP_ENABLED).
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mx-auto w-full max-w-5xl px-4 py-6">
        <h1 className="flex items-center gap-2 text-xl font-bold text-ink-900">
          <ClockIcon size={20} className="text-brand-700" /> Beheer TimeChimp
        </h1>
        <p className="mt-1 text-sm text-ink-900/65">
          Koppeling met TimeChimp: connectiestatus, de koppeling per medewerker en het
          weekoverzicht. De uren zelf beheer je in TimeChimp.
        </p>

        {/* Connectiestatus */}
        <div className="mt-4 rounded-2xl border border-ring2 bg-white p-4 shadow-card">
          {!status ? (
            <p className="text-sm text-ink-900/55">Connectiestatus laden…</p>
          ) : status.reachable ? (
            <div className="text-sm">
              <p className="inline-flex items-center gap-1.5 font-medium text-ink-900">
                <CheckCircle2Icon size={16} className="text-brand-700" /> TimeChimp is bereikbaar
              </p>
              <p className="mt-1 text-ink-900/65">
                {status.matchedCount} van de {status.medewerkers.length} interne medewerkers
                gekoppeld; {status.timechimpUserCount} actieve gebruikers in TimeChimp.
              </p>
            </div>
          ) : (
            <p className="inline-flex items-center gap-1.5 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertCircleIcon size={14} /> {status.message}
            </p>
          )}
        </div>

        {/* Koppeling per medewerker — bepaalt wiens uren geschreven worden */}
        {status && (
          <div className="mt-4 rounded-2xl border border-ring2 bg-white p-4 shadow-card">
            <h2 className="text-sm font-bold text-ink-900">Koppeling per medewerker</h2>
            <p className="mt-1 text-sm text-ink-900/65">
              Standaard koppelt de kennisbank op e-mailadres en onthoudt daarna het
              TimeChimp-account, zodat een nieuw e-mailadres de koppeling niet breekt. Wijkt het
              adres af, kies dan hier zelf het juiste account. {telling}
            </p>
            {koppelMsg && (
              <p className="mt-2 rounded-md bg-brand-50 px-3 py-2 text-sm text-brand-900">
                {koppelMsg}
              </p>
            )}
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[42rem] text-sm">
                <thead>
                  <tr className="border-b border-ring2 text-left text-xs uppercase text-ink-900/55">
                    <th className="py-2 pr-3 font-semibold">Medewerker</th>
                    <th className="py-2 pr-3 font-semibold">Status</th>
                    <th className="py-2 font-semibold">TimeChimp-account</th>
                  </tr>
                </thead>
                <tbody>
                  {gesorteerd.map((m) => (
                    <tr key={m.userId} className="border-b border-ring2/60 align-top">
                      <td className="py-2 pr-3">
                        <span className="font-medium text-ink-900">{m.displayName}</span>
                        <span className="block text-xs text-ink-900/55">{m.email}</span>
                      </td>
                      <td className="py-2 pr-3">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-xs ${STATUS_STIJL[m.status]}`}
                        >
                          {STATUS_LABEL[m.status]}
                        </span>
                        {m.koppelingDoor && m.koppelingDoor !== 'automatisch' && (
                          <span className="block text-xs text-ink-900/55">handmatig gezet</span>
                        )}
                      </td>
                      <td className="py-2">
                        <select
                          value={m.status === 'geen' ? 'geen' : (m.timechimpUserId ?? '')}
                          disabled={bezig === m.userId}
                          onChange={(e) => void koppel(m, e.target.value)}
                          className="w-full max-w-sm rounded-lg border border-ring2 bg-white px-3 py-1.5 text-sm text-ink-900 disabled:opacity-50"
                        >
                          <option value="">Nog niet gekoppeld</option>
                          <option value="geen">Geen koppeling (verbergt Uren)</option>
                          {/* Een verlopen id staat niet in accounts; zonder deze
                              optie viel de keuzelijst terug op "Nog niet
                              gekoppeld" en sprak zo de status ernaast tegen. */}
                          {m.status === 'verlopen' && m.timechimpUserId !== null && (
                            <option value={m.timechimpUserId}>
                              Onbekend account #{m.timechimpUserId} (verlopen)
                            </option>
                          )}
                          {(() => {
                            // Bovenaan wat op de naam lijkt: de reden dat deze
                            // medewerker niet automatisch koppelt is nou juist
                            // dat zijn e-mailadres afwijkt, dus alfabetisch
                            // zoeken helpt niet.
                            const { voorgesteld, overig } = suggesties(m, status.accounts);
                            const optie = (a: (typeof status.accounts)[number]) => (
                              <option key={a.id} value={a.id}>
                                {a.displayName} · {a.email}
                                {a.gekoppeldAan && a.id !== m.timechimpUserId
                                  ? ` (al aan ${a.gekoppeldAan})`
                                  : ''}
                              </option>
                            );
                            return (
                              <>
                                {voorgesteld.length > 0 && (
                                  <optgroup label="Waarschijnlijk deze">
                                    {voorgesteld.map(optie)}
                                  </optgroup>
                                )}
                                {overig.length > 0 && (
                                  <optgroup
                                    label={voorgesteld.length > 0 ? 'Alle accounts' : 'Accounts'}
                                  >
                                    {overig.map(optie)}
                                  </optgroup>
                                )}
                              </>
                            );
                          })()}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Toewijzingen — bepaalt wat medewerkers in hun keuzelijst zien */}
        {status && (
          <div className="mt-4 rounded-2xl border border-ring2 bg-white p-4 shadow-card">
            <h2 className="text-sm font-bold text-ink-900">Toegewezen klanten en projecten</h2>
            <p className="mt-1 text-sm text-ink-900/65">
              Medewerkers zien in hun urenscherm alleen de projecten waaraan ze in TimeChimp zijn
              toegewezen. Die lijst wordt elke nacht opgehaald.
            </p>

            {!status.toewijzingen.aanwezig ? (
              <p className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <AlertCircleIcon size={14} />
                Nog geen toewijzingen opgehaald. Medewerkers zien tot vannacht alleen de projecten
                waarop ze eerder uren schreven.
              </p>
            ) : (
              <p className="mt-2 text-sm text-ink-900/65">
                Laatst bijgewerkt:{' '}
                <span className="font-medium text-ink-900">
                  {new Date(status.toewijzingen.sweptAt!).toLocaleString('nl-NL', {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </span>{' '}
                · {status.toewijzingen.projectCount} projecten, {status.toewijzingen.userCount}{' '}
                medewerkers.
              </p>
            )}

            {status.toewijzingen.verouderd && (
              <p className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                <AlertCircleIcon size={14} />
                De lijst is langer dan twee dagen niet bijgewerkt. Waarschuw de beheerder: de
                nachtelijke taak hapert.
              </p>
            )}

            {status.toewijzingen.voortgang && (
              <p className="mt-2 text-xs text-ink-900/55">
                Bezig met ophalen: {status.toewijzingen.voortgang.gedaan} van{' '}
                {status.toewijzingen.voortgang.totaal} projecten.
              </p>
            )}

            {projecten.length > 0 && (
              <div className="mt-3 border-t border-ring2 pt-3">
                <p className="text-xs text-ink-900/65">
                  Net iemand aan een project gekoppeld in TimeChimp? Ververs dat project, dan is het
                  direct zichtbaar in plaats van pas morgen.
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <select
                    value={refreshProjectId}
                    onChange={(e) => setRefreshProjectId(e.target.value)}
                    className="min-w-0 flex-1 rounded-lg border border-ring2 bg-white px-3 py-1.5 text-sm text-ink-900"
                  >
                    <option value="">Kies een project</option>
                    {projecten.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.customerName ? `${p.customerName} · ${p.name}` : p.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => void ververs()}
                    disabled={!refreshProjectId || refreshing}
                    className="rounded-lg bg-brand-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                  >
                    {refreshing ? 'Bezig…' : 'Ververs project'}
                  </button>
                </div>
                {refreshMsg && <p className="mt-2 text-xs text-ink-900/65">{refreshMsg}</p>}
              </div>
            )}
          </div>
        )}

        {/* Weekrapportage */}
        <div className="mt-6 mb-4 flex items-center justify-between rounded-2xl border border-ring2 bg-white px-4 py-3 shadow-card">
          <button
            type="button"
            onClick={() => setMonday((m) => addDays(m, -7))}
            className="rounded-lg border border-ring2 bg-white p-1.5 text-ink-900 hover:bg-brand-50"
            aria-label="Vorige week"
          >
            <ChevronLeftIcon size={16} />
          </button>
          <p className="text-sm font-semibold text-ink-900">{weekTitle}</p>
          <button
            type="button"
            onClick={() => setMonday((m) => addDays(m, 7))}
            className="rounded-lg border border-ring2 bg-white p-1.5 text-ink-900 hover:bg-brand-50"
            aria-label="Volgende week"
          >
            <ChevronRightIcon size={16} />
          </button>
        </div>

        {errorMsg && (
          <p className="mb-4 inline-flex items-center gap-1.5 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertCircleIcon size={14} /> {errorMsg}
          </p>
        )}

        <div className="overflow-x-auto rounded-2xl border border-ring2 bg-white shadow-card">
          {loading ? (
            <div className="px-6 py-10 text-center text-sm text-ink-900/55">Laden…</div>
          ) : rows.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-ink-900/55">
              Geen interne medewerkers gevonden.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ring2 text-left text-[12px] uppercase tracking-wide text-ink-900/55">
                  <th className="px-4 py-2 font-medium">Medewerker</th>
                  <th className="px-4 py-2 text-right font-medium">Contract p/w</th>
                  <th className="px-4 py-2 text-right font-medium">Geschreven</th>
                  <th className="px-4 py-2 text-right font-medium">Aangeboden</th>
                  <th className="px-4 py-2 font-medium">Signaal</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const behind = r.matched && r.weekHours !== null && r.writtenHours < r.weekHours;
                  return (
                    <tr key={r.email} className="border-b border-ring2 last:border-b-0">
                      <td className="px-4 py-2">
                        <span className="font-medium text-ink-900">{r.displayName}</span>
                        <span className="ml-2 text-xs text-ink-900/55">{r.email}</span>
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-ink-900/70">
                        {r.weekHours !== null ? r.weekHours.toLocaleString('nl-NL') : '–'}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-ink-900">
                        {r.writtenHours.toLocaleString('nl-NL')}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-ink-900">
                        {r.submittedHours.toLocaleString('nl-NL')}
                      </td>
                      <td className="px-4 py-2">
                        {!r.matched ? (
                          <span className="rounded-sm bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
                            Geen match
                          </span>
                        ) : behind ? (
                          <span className="rounded-sm bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                            Achter op contract
                          </span>
                        ) : (
                          <span className="rounded-sm bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
                            Op schema
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <p className="mt-3 text-xs text-ink-900/55">
          De uren komen live uit TimeChimp en omvatten alle registraties in de gekozen week,
          ongeacht of ze via de kennisbank of TimeChimp zelf zijn geschreven.
        </p>
      </div>
    </Layout>
  );
}
